import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiCalculator, FiCalendar, FiDollarSign, FiClock, FiArrowRight, FiAlertCircle } = FiIcons;

function App() {
  const [step, setStep] = useState(1);
  const [tripDays, setTripDays] = useState('');
  const [pricePerDay, setPricePerDay] = useState('');
  const [extensionHours, setExtensionHours] = useState('');
  const [result, setResult] = useState(null);
  const [cappedAtTripPrice, setCappedAtTripPrice] = useState(false);
  const [isMinimumApplied, setIsMinimumApplied] = useState(false);

  const calculateExtensionCost = () => {
    const dailyRate = parseFloat(pricePerDay);
    const hours = parseFloat(extensionHours);
    const tripPrice = dailyRate * parseInt(tripDays);
    const minimumCost = 35;
    
    // Calculate extension cost using the formula
    let extensionCost = dailyRate * (0.50 + 0.05 * hours);
    
    // Check if minimum applies
    const belowMinimum = extensionCost < minimumCost;
    
    // Apply minimum if needed
    if (belowMinimum) {
      extensionCost = minimumCost;
      setIsMinimumApplied(true);
    } else {
      setIsMinimumApplied(false);
    }
    
    // Cap at trip price if extension exceeds it
    if (extensionCost > tripPrice) {
      extensionCost = tripPrice;
      setCappedAtTripPrice(true);
    } else {
      setCappedAtTripPrice(false);
    }
    
    setResult(extensionCost);
    setStep(4);
  };

  const resetCalculator = () => {
    setStep(1);
    setTripDays('');
    setPricePerDay('');
    setExtensionHours('');
    setResult(null);
    setCappedAtTripPrice(false);
    setIsMinimumApplied(false);
  };

  const canProceedToStep2 = tripDays && parseInt(tripDays) > 0;
  const canProceedToStep3 = pricePerDay && parseFloat(pricePerDay) > 0;
  const canCalculate = extensionHours && parseFloat(extensionHours) >= 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCalculator} className="text-2xl text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Trip Extension Calculator</h1>
          <p className="text-gray-600">Calculate your extension costs in 3 simple steps</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            {[1, 2, 3].map((stepNum) => (
              <div
                key={stepNum}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step >= stepNum ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {stepNum}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${((step - 1) / 3) * 100}%` }}
              className="bg-blue-600 h-2 rounded-full"
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Step 1: Trip Days */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <SafeIcon icon={FiCalendar} className="text-3xl text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">How many trip days?</h2>
              <p className="text-gray-600 text-sm">Enter the total number of days for your trip</p>
            </div>
            <div className="space-y-4">
              <input
                type="number"
                value={tripDays}
                onChange={(e) => setTripDays(e.target.value)}
                placeholder="Enter number of days"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
                min="1"
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => canProceedToStep2 && setStep(2)}
                disabled={!canProceedToStep2}
                className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                  canProceedToStep2
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
                <SafeIcon icon={FiArrowRight} className="text-lg" />
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Price Per Day */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <SafeIcon icon={FiDollarSign} className="text-3xl text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Price per trip day?</h2>
              <p className="text-gray-600 text-sm">Enter your average daily rate</p>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                <input
                  type="number"
                  value={pricePerDay}
                  onChange={(e) => setPricePerDay(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => canProceedToStep3 && setStep(3)}
                  disabled={!canProceedToStep3}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                    canProceedToStep3
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue
                  <SafeIcon icon={FiArrowRight} className="text-lg" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Extension Hours */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="text-center">
              <SafeIcon icon={FiClock} className="text-3xl text-purple-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Extension hours?</h2>
              <p className="text-gray-600 text-sm">How many hours do you want to extend?</p>
            </div>
            <div className="space-y-4">
              <input
                type="number"
                value={extensionHours}
                onChange={(e) => setExtensionHours(e.target.value)}
                placeholder="Enter hours"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
                min="0"
                step="0.5"
              />
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setStep(2)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={calculateExtensionCost}
                  disabled={!canCalculate}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                    canCalculate
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Calculate
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Results */}
        {step === 4 && result !== null && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiDollarSign} className="text-2xl text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Extension Cost</h2>
            </div>

            {/* Warning Messages */}
            {(cappedAtTripPrice || isMinimumApplied) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-2"
              >
                {isMinimumApplied && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <SafeIcon icon={FiAlertCircle} className="text-blue-600 text-lg mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <span className="font-semibold">Minimum cost applied:</span> Extension cost set to $35.00 minimum.
                    </div>
                  </div>
                )}
                {cappedAtTripPrice && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <SafeIcon icon={FiAlertCircle} className="text-amber-600 text-lg mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <span className="font-semibold">Cost capped:</span> Extension cost cannot exceed total trip price of ${(parseFloat(pricePerDay) * parseInt(tripDays)).toFixed(2)}.
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  ${result.toFixed(2)}
                </div>
                <p className="text-gray-600">Total extension cost</p>
              </div>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Trip Days:</span>
                  <span>{tripDays} days</span>
                </div>
                <div className="flex justify-between">
                  <span>Daily Rate:</span>
                  <span>${parseFloat(pricePerDay).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total Trip Price:</span>
                  <span>${(parseFloat(pricePerDay) * parseInt(tripDays)).toFixed(2)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between">
                  <span>Extension Hours:</span>
                  <span>{extensionHours} hours</span>
                </div>
                <div className="flex justify-between">
                  <span>Formula:</span>
                  <span>${pricePerDay} × (0.50 + 0.05 × {extensionHours})</span>
                </div>
                <div className="flex justify-between">
                  <span>Multiplier:</span>
                  <span>{(0.50 + 0.05 * parseFloat(extensionHours)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Calculated Cost:</span>
                  <span>${(parseFloat(pricePerDay) * (0.50 + 0.05 * parseFloat(extensionHours))).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Minimum Cost:</span>
                  <span>$35.00</span>
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={resetCalculator}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Calculate Another Extension
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

export default App;